import { Download, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, Department, EvaluationCriteriaVersion, EvaluationCriterionCommand, Project, ScopePerformance, Session, Task, User } from "../api";
import { isoDate } from "../lib/format";
import { DataTable, Empty, Loading, Meter, ModalFrame, PageHead } from "../components/ui";
import { departmentWithin, userManagesWithin } from "../lib/org";
import { periods } from "./Performance";

export function EvaluationPage({ session, users, departments, projects, tasks }: {
  session: Session; users: User[]; departments: Department[]; projects: Project[]; tasks: Task[];
}) {
  const { t, i18n } = useTranslation();
  const options = useMemo(() => periods(new Date(), i18n.resolvedLanguage || i18n.language), [i18n.language, i18n.resolvedLanguage]);
  const [periodKey, setPeriodKey] = useState(options[0].key);
  const actor = users.find((user) => user.id === session.userId);
  const visibleDepartments = session.roles.includes("OWNER")
    ? departments
    : session.roles.includes("MANAGER")
      ? departments.filter((department) => Boolean(actor && userManagesWithin(actor, department.id, departments)))
      : [];
  const visibleProjects = projects.filter((project) => session.roles.includes("OWNER") || project.members.some((member) => member.userId === session.userId && member.role === "PM"));
  const scopes = [
    ...visibleDepartments.map((department) => ({ key: `DEPARTMENT:${department.id}`, label: department.name })),
    ...visibleProjects.map((project) => ({ key: `PROJECT:${project.id}`, label: `${project.code} · ${project.name}` })),
  ];
  const [scopeKey, setScopeKey] = useState(scopes[0]?.key || "");
  const [data, setData] = useState<ScopePerformance | null>(null);
  const [error, setError] = useState("");
  const [criteria, setCriteria] = useState<EvaluationCriteriaVersion | null>(null);
  const period = options.find((item) => item.key === periodKey) || options[0];

  useEffect(() => {
    if (!scopeKey) return;
    setData(null); setError("");
    const [type, id] = scopeKey.split(":");
    const request = type === "DEPARTMENT" ? api.departmentPerformance(id, period.from, period.to) : api.projectPerformance(id, period.from, period.to);
    void request.then(setData).catch((reason) => setError(reason instanceof Error ? reason.message : t("evaluation.loadError")));
  }, [scopeKey, period.from, period.to]);
  useEffect(() => {
    void api.currentEvaluationCriteria().then(setCriteria)
      .catch((reason) => setError(reason instanceof Error ? reason.message : t("evaluation.criteriaError")));
  }, []);

  function exportCsv() {
    if (!data) return;
    const header = [t("evaluation.member"), t("evaluation.taskCount"), t("evaluation.quality"), t("evaluation.onTime")];
    const rows = data.members.map((member) => [member.displayName, String(member.taskCount), member.averageQualityScore?.toFixed(1) ?? "", member.onTimeRate === null || member.onTimeRate === undefined ? "" : String(Math.round(member.onTimeRate))]);
    const csv = [header, ...rows].map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `evaluation-${data.scopeName}-${period.from}-${period.to}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const scopeTasks = useMemo(() => {
    if (!scopeKey) return [] as Task[];
    const [type, id] = scopeKey.split(":");
    return type === "PROJECT"
      ? tasks.filter((task) => task.projectId === id)
      : tasks.filter((task) => departmentWithin(id, task.departmentId, departments) || task.departmentId === id);
  }, [scopeKey, tasks, departments]);
  const excluded = scopeTasks.filter((task) => !task.countsForEvaluation);

  const criteriaPanel = <EvaluationCriteriaPanel criteria={criteria} />;

  if (!scopes.length) return <>
    <PageHead title={t("evaluation.title")} meta={t("evaluation.metaRestricted")} />
    {criteriaPanel}
    <Empty title={t("evaluation.noScope")} copy={t("evaluation.noScopeHelp")} />
  </>;

  return <>
    <PageHead title={t("evaluation.title")} meta={`${data?.scopeName || t("evaluation.scope")} · ${period.from} — ${period.to}`} controls={<>
      <select aria-label={t("evaluation.period")} value={periodKey} onChange={(event) => setPeriodKey(event.target.value)}>{options.map((item) => <option value={item.key} key={item.key}>{item.label}</option>)}</select>
      <select aria-label={t("evaluation.scope")} value={scopeKey} onChange={(event) => setScopeKey(event.target.value)}>{scopes.map((scope) => <option value={scope.key} key={scope.key}>{scope.label}</option>)}</select>
      <button type="button" className="secondary" disabled={!data} onClick={exportCsv}><Download size={14} /> CSV</button>
    </>} />
    {error && <div className="global-error">{error}</div>}
    {criteriaPanel}
    {!data ? <Loading layout="summary" label={t("evaluation.loading")} /> : <>
      <div className="stat-row">
        <div className="stat"><div className="mono-label">{t("evaluation.taskCount")}</div><strong>{data.taskCount}</strong><small>{t("evaluation.taskCountHelp")}</small></div>
        <div className="stat"><div className="mono-label">{t("evaluation.qualityAverage")}</div><strong>{data.averageQualityScore === null || data.averageQualityScore === undefined ? "-" : `${data.averageQualityScore.toFixed(1)} / 5`}</strong><small>{t("evaluation.qualityHelp")}</small></div>
        <div className="stat"><div className="mono-label">{t("evaluation.onTime")}</div><strong>{data.onTimeRate === null || data.onTimeRate === undefined ? "-" : `${Math.round(data.onTimeRate)}%`}</strong><small>{t("evaluation.onTimeHelp")}</small></div>
        <div className="stat"><div className="mono-label">{t("evaluation.excluded")}</div><strong>{excluded.length}</strong><small>{t("evaluation.excludedHelp")}</small></div>
      </div>

      <section className="section-card">
        <header><div><span className="mono-label">MEMBERS</span><h2>{t("evaluation.members")}</h2></div><span className="mono-label">{t("evaluation.membersHelp")}</span></header>
        <p className="permission-note">{t("evaluation.csvHelp")}</p>
        <DataTable cols="minmax(180px, 2fr) 110px minmax(160px, 1fr) minmax(160px, 1fr)">
          <div className="data-row data-head"><span>{t("evaluation.member")}</span><span>{t("evaluation.taskCount")}</span><span>{t("evaluation.quality")}</span><span>{t("evaluation.onTime")}</span></div>
          {data.members.map((member) => <div className="data-row" key={member.userId}>
            <span className="title-cell"><b>{member.displayName}</b><small>{users.find((user) => user.id === member.userId)?.jobTitle || t("evaluation.member")}</small></span>
            <span className="mono">{t("evaluation.count", { count: member.taskCount })}</span>
            <span className="cell-meter"><span className="mono">{member.averageQualityScore?.toFixed(1) ?? "-"}</span><Meter ratio={(member.averageQualityScore || 0) / 5 * 100} /></span>
            <span className="cell-meter"><span className="mono">{member.onTimeRate === null || member.onTimeRate === undefined ? "-" : `${Math.round(member.onTimeRate)}%`}</span><Meter ratio={member.onTimeRate || 0} /></span>
          </div>)}
        </DataTable>
        {data.members.length === 0 && <Empty title={t("evaluation.noMembers")} copy={t("evaluation.noMembersHelp")} />}
      </section>

      <section className="section-card">
        <header><div><span className="mono-label">EXCLUDED</span><h2>{t("evaluation.excludedTitle", { count: excluded.length })}</h2></div></header>
        <p className="permission-note">{t("evaluation.excludedSectionHelp")}</p>
        <DataTable cols="minmax(220px, 2fr) minmax(160px, 1fr) minmax(120px, 1fr) 110px">
          <div className="data-row data-head"><span>{t("evaluation.task")}</span><span>{t("evaluation.reason")}</span><span>{t("evaluation.assignee")}</span><span>{t("evaluation.due")}</span></div>
          {excluded.map((task) => <div className="data-row" key={task.id}>
            <span className="title-cell"><b>{task.internalTitle}</b></span>
            <span>{task.evaluationExclusionReason || t("evaluation.notProvided")}</span>
            <span>{task.assigneeIds.map((id) => users.find((user) => user.id === id)?.displayName || t("evaluation.member")).join(", ") || t("evaluation.unassigned")}</span>
            <span className="mono">{isoDate(task.dueOn) || t("evaluation.unassigned")}</span>
          </div>)}
        </DataTable>
        {excluded.length === 0 && <p className="permission-note">{t("evaluation.noExcluded")}</p>}
      </section>

    </>}
  </>;
}

export function EvaluationCriteriaSettings({ onChanged }: { onChanged: (message: string) => Promise<void> }) {
  const [criteria, setCriteria] = useState<EvaluationCriteriaVersion | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    void api.currentEvaluationCriteria().then(setCriteria)
      .catch((reason) => setError(reason instanceof Error ? reason.message : "평가 스키마를 불러오지 못했습니다."));
  }, []);

  return <>
    {error && <div className="global-error">{error}<button type="button" onClick={() => setError("")}>닫기</button></div>}
    <section className="section-card">
      <header><div><span className="mono-label">EVALUATION SCHEMA</span><h2>평가 스키마</h2></div>
        <button type="button" className="primary" disabled={!criteria} onClick={() => setEditing(true)}><Pencil size={14} /> 스키마 수정</button>
      </header>
      <p className="permission-note">수행·검증 승인 시 사용하는 평가 항목과 만점입니다. 수정 내용을 적용하면 새 버전이 발행되고, 이미 끝난 평가는 당시 스키마와 점수를 그대로 보존합니다.</p>
    </section>
    <EvaluationCriteriaPanel criteria={criteria} />
    {editing && criteria && <EvaluationCriteriaModal current={criteria} onClose={() => setEditing(false)} onSaved={(saved) => {
      setCriteria(saved); setEditing(false); void onChanged(`평가 스키마 v${saved.version}을 적용했습니다.`);
    }} />}
  </>;
}

function EvaluationCriteriaPanel({ criteria }: { criteria: EvaluationCriteriaVersion | null }) {
  const { t } = useTranslation();
  return <section className="section-card evaluation-criteria-card">
    <header><div><span className="mono-label">QUALITY RUBRIC</span><h2>{t("evaluation.criteria")}</h2></div><span className="mono-label">{criteria ? t("evaluation.active", { version: criteria.version }) : t("common.loading")}</span></header>
    {!criteria ? <Loading layout="form" label={t("evaluation.loadingCriteria")} /> : <div className="evaluation-criteria-groups">
      {(["PROGRESS", "VERIFICATION"] as const).map((group) => <section key={group}>
        <div><b>{t(group === "PROGRESS" ? "evaluation.progress" : "evaluation.verification")}</b><small>{t(group === "PROGRESS" ? "evaluation.progressHelp" : "evaluation.verificationHelp")}</small></div>
        {criteria.criteria.filter((criterion) => criterion.group === group).map((criterion) => <article key={criterion.id}>
          <div><b>{criterion.label}</b><small>{criterion.guidance || t("evaluation.noGuidance")}</small></div><em>{t("evaluation.points", { score: criterion.maxScore })}</em>
        </article>)}
      </section>)}
    </div>}
  </section>;
}

function EvaluationCriteriaModal({ current, onClose, onSaved }: {
  current: EvaluationCriteriaVersion; onClose: () => void; onSaved: (saved: EvaluationCriteriaVersion) => void;
}) {
  const [draft, setDraft] = useState<EvaluationCriterionCommand[]>(() => current.criteria.map((criterion) => ({
    label: criterion.label, group: criterion.group,
    guidance: criterion.guidance, maxScore: criterion.maxScore, sortOrder: criterion.sortOrder,
  })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const update = (index: number, values: Partial<EvaluationCriterionCommand>) => setDraft((all) => all.map((item, itemIndex) => itemIndex === index ? { ...item, ...values } : item));
  const add = (group: EvaluationCriterionCommand["group"]) => setDraft((all) => {
    return [...all, { label: "", group, guidance: "", maxScore: 5, sortOrder: all.length }];
  });
  const save = async () => {
    if (!draft.length || draft.some((item) => !item.label.trim() || Number(item.maxScore) <= 0)) {
      setError("기준명과 0보다 큰 만점을 모두 입력해 주세요."); return;
    }
    setBusy(true); setError("");
    try {
      const normalized = draft.map((item, index) => ({ ...item, label: item.label.trim(), guidance: item.guidance.trim(), maxScore: Number(item.maxScore), sortOrder: index }));
      onSaved(await api.createEvaluationCriteriaVersion(normalized, true));
    } catch (reason) { setError(reason instanceof Error ? reason.message : "평가 기준을 저장하지 못했습니다."); }
    finally { setBusy(false); }
  };
  return <ModalFrame title="평가 기준 관리" eyebrow={`PUBLISH VERSION ${current.version + 1}`} wide onClose={onClose}>
    <div className="evaluation-criteria-editor">
      <p>저장하면 새 버전으로 즉시 적용됩니다. 과거 평가에는 당시 기준과 점수가 그대로 보존됩니다.</p>
      {(["PROGRESS", "VERIFICATION"] as const).map((group) => <section key={group}>
        <header><div><b>{group === "PROGRESS" ? "수행 평가" : "검증 평가"}</b><small>{group === "PROGRESS" ? "수행보고 승인" : "검증보고 승인"} 시 입력할 항목</small></div><button type="button" className="secondary" onClick={() => add(group)}><Plus size={13} /> 추가</button></header>
        <div className="criteria-editor-list">
          {draft.map((criterion, index) => criterion.group === group && <div className="criteria-editor-row" key={`${group}-${index}`}>
            <label>기준명<input value={criterion.label} maxLength={120} onChange={(event) => update(index, { label: event.target.value })} /></label>
            <label>평가 안내<input value={criterion.guidance} maxLength={2000} onChange={(event) => update(index, { guidance: event.target.value })} /></label>
            <label>만점<input type="number" min="0.01" max="100" step="0.5" value={criterion.maxScore} onChange={(event) => update(index, { maxScore: Number(event.target.value) })} /></label>
            <button type="button" className="danger-quiet" aria-label={`${criterion.label || "새 평가 기준"} 삭제`} onClick={() => setDraft((all) => all.filter((_, itemIndex) => itemIndex !== index))}><Trash2 size={14} /></button>
          </div>)}
        </div>
      </section>)}
      {error && <div className="error-box">{error}</div>}
      <footer><button type="button" onClick={onClose}>취소</button><button type="button" className="primary" disabled={busy} onClick={() => void save()}>{busy ? "저장 중…" : "새 버전 적용"}</button></footer>
    </div>
  </ModalFrame>;
}
